import type { FilterType, SearchResult } from '@elastic/search-ui/lib/esm/types'
import { Col } from 'react-bootstrap'
import { chapterString, codesystems, collectionsString } from 'utils'
import {
	ResultViewWrapper,
	SearchResultBar,
	TableHeader,
	TableHeaderContainer,
} from './style'
import { useStoreContext } from 'context/StoreProvider'
import { useLocation, useNavigate } from 'react-router-dom'
import { handleNavigate } from '../../utils'
import { useTranslation } from 'react-i18next'
import { withSearch } from '@elastic/react-search-ui'
import KisSearchResultRow from './KisSearchResultRow'
import SearchResultRow from './SearchResultRow'
import { CodeSystem } from 'context/elasticsearch/types'

interface CodeSystemProp {
	codeSystem: CodeSystem
	displayScore: boolean
}

export interface SearchResultTableProps {
	searchTerm?: string
	addFilter: (
		field: string,
		value: string,
		type: FilterType | undefined
	) => void
	results: SearchResult
}

const SearchResultTable = ({
	results,
	searchTerm,
	addFilter,
	codeSystem,
	displayScore,
}: SearchResultTableProps & CodeSystemProp) => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	const { actions, state } = useStoreContext()
	const { excludedSearchFields } = state
	const { updateCodeValue } = actions

	const isKis = codeSystem === CodeSystem.Adm

	return (
		<SearchResultBar $isKis={isKis} role='table' className='position-relative'>
			<TableHeaderContainer role='rowgroup'>
				<TableHeader role='row' className='gap-1 gap-md-3 flex-nowrap'>
					{isKis ? (
						<>
							<Col role='columnheader' className='p-0' xs={1}>
								<span>{t('general.id')}</span>
							</Col>
							<Col role='columnheader' xs={3} className='p-0'>
								<span>{t('general.codesystem')}</span>
							</Col>
							<Col role='columnheader' xs={2} className='p-0'>
								<span>{t('search.admTable.owner')}</span>
							</Col>
							<Col role='columnheader' xs={3} md={2} className='p-0'>
								<span>{t('search.admTable.collection')}</span>
							</Col>
							<Col role='columnheader' xs={2} className='p-0'>
								<span>{t('search.sorting.category')}</span>
							</Col>
							<Col role='columnheader' xs={1} className='text-end p-lg-0'>
								<span>{t('search.admTable.status')}</span>
							</Col>
						</>
					) : (
						<>
							{displayScore && (
								<Col role='columnheader' xs='auto' className='p-0'>
									<span>Score</span>
								</Col>
							)}
							<Col role='columnheader' xs='auto' className='p-0'>
								<span>{t('general.codeValue')}</span>
							</Col>
							<Col role='columnheader' xs='auto' className='p-0'>
								<span>{t('general.codeText')}</span>
							</Col>
						</>
					)}
				</TableHeader>
			</TableHeaderContainer>
			{results.map((result: SearchResult, idx: number) => {
				const shouldBeDisabled =
					codeSystem !== CodeSystem.Adm && // Never disabled for KIS and NORPAT
					codeSystem !== CodeSystem.Norpat &&
					result.active?.raw === false

				const handleClickedResult = () => {
					if (isKis) {
						navigate(`./${collectionsString}/${result.code_system_oid.raw}`)
						updateCodeValue(result.code_system_oid.raw)
					} else if (result.code_system.raw === codesystems.icpc2b) {
						navigate(
							`${handleNavigate(result.code_system.raw)}/${chapterString}/${
								result.parent_code.raw
							}`
						)
						updateCodeValue(result.parent_code.raw)
					} else if (codeSystem === CodeSystem.Icd10) {
						if (shouldBeDisabled) return null
						navigate(`./${chapterString}/${result.code.raw}`)
						updateCodeValue(result.code.raw)
					} else {
						if (shouldBeDisabled) {
							return null
						} else {
							navigate(
								`${handleNavigate(
									result.code_system.raw
								)}/${chapterString}/${result.code.raw.replace('_', '')}`
							)
							updateCodeValue(result.code.raw)
						}
					}
				}

				const keyValue = isKis
					? result.code_system_oid?.raw || `kis-${idx}`
					: result.code?.raw || `code-${idx}`

				return (
					<ResultViewWrapper
						$isDisabled={shouldBeDisabled}
						onClick={handleClickedResult}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								handleClickedResult()
							}
						}}
						action={!shouldBeDisabled}
						key={keyValue}
						role='rowgroup'
						className='w-100'
					>
						{isKis ? (
							<KisSearchResultRow
								result={result}
								searchTerm={searchTerm}
								showCodes={isKis && !excludedSearchFields.includes('code')}
								displayScore={displayScore}
							/>
						) : (
							<SearchResultRow
								result={result}
								searchTerm={searchTerm}
								addFilter={addFilter}
								displayScore={displayScore}
								codeSystem={codeSystem}
							/>
						)}
					</ResultViewWrapper>
				)
			})}
		</SearchResultBar>
	)
}

export default withSearch<CodeSystemProp, SearchResultTableProps>(
	({ searchTerm, results, addFilter }) => ({
		searchTerm,
		results,
		addFilter,
	})
)(SearchResultTable)
